import { Vector } from "./Vector";

/**
* A 3x3 matrix. Immutable
*/
export class Matrix {
    private elems: number[];

    /**
    * Creates an instance of Matrix
    * 
    * @constructor
    * @param {number[]} elements Array of length 9 containing the element values
    */
    constructor(elements: number[]) {
        this.elems = [];
        for (let e of elements) {
            this.elems.push(e);
        }
    }

    static fromRotationAboutAxis(axis: number, angle: number): Matrix {
        let sine = Math.sin(angle);
        let cosine = Math.cos(angle);

        if (axis == 0)
            return new Matrix([1, 0, 0, 0, cosine, -sine, 0, sine, cosine]);
        if (axis == 1)
            return new Matrix([cosine, 0, sine, 0, 1, 0, -sine, 0, cosine]);
        else
            return new Matrix([cosine, -sine, 0, sine, cosine, 0, 0, 0, 1]);

    }

    static fromArbitraryAxisAndRotation(axis: Vector, angle: number): Matrix {
        let x2 = axis.x * axis.x;
        let y2 = axis.y * axis.y;
        let z2 = axis.z * axis.z;
        let sine = Math.sin(angle);
        let cosine = Math.cos(angle);
        let oneMinusCos = 1 - cosine;

        let el00 = x2 + (1 - x2) * cosine;
        let el01 = axis.x * axis.y * oneMinusCos - axis.z * sine;
        let el02 = axis.x * axis.z * oneMinusCos + axis.y * sine;

        let el10 = axis.x * axis.y * oneMinusCos + axis.z * sine;
        let el11 = y2 + (1 - y2) * cosine;
        let el12 = axis.y * axis.z * oneMinusCos - axis.x * sine;

        let el20 = axis.x * axis.z * oneMinusCos - axis.y * sine;
        let el21 = axis.y * axis.z * oneMinusCos + axis.x * sine;
        let el22 = z2 + (1 - z2) * cosine;

        return new Matrix([el00, el01, el02, el10, el11, el12, el20, el21, el22]);
    }

    /**
    * 
    * @param v - the Vector to be transformed
    */
    multiplyVector(v: Vector): Vector {
        return new Vector(this.elems[0] * v.x + this.elems[1] * v.y + this.elems[2] * v.z,
                          this.elems[3] * v.x + this.elems[4] * v.y + this.elems[5] * v.z,
                          this.elems[6] * v.x + this.elems[7] * v.y + this.elems[8] * v.z);
        }

    multiplyMatrix(mat2: Matrix): Matrix {
        return new Matrix([
                this.elems[0] * mat2.elems[0] + this.elems[1] * mat2.elems[3] + this.elems[2] * mat2.elems[6],
                this.elems[0] * mat2.elems[1] + this.elems[1] * mat2.elems[4] + this.elems[2] * mat2.elems[7],
                this.elems[0] * mat2.elems[2] + this.elems[1] * mat2.elems[5] + this.elems[2] * mat2.elems[8],
                this.elems[3] * mat2.elems[0] + this.elems[4] * mat2.elems[3] + this.elems[5] * mat2.elems[6],
                this.elems[3] * mat2.elems[1] + this.elems[4] * mat2.elems[4] + this.elems[5] * mat2.elems[7],
                this.elems[3] * mat2.elems[2] + this.elems[4] * mat2.elems[5] + this.elems[5] * mat2.elems[8],
                this.elems[6] * mat2.elems[0] + this.elems[7] * mat2.elems[3] + this.elems[8] * mat2.elems[6],
                this.elems[6] * mat2.elems[1] + this.elems[7] * mat2.elems[4] + this.elems[8] * mat2.elems[7],
                this.elems[6] * mat2.elems[2] + this.elems[7] * mat2.elems[5] + this.elems[8] * mat2.elems[8]]);
    }

    transpose(): Matrix {
        return new Matrix(
                [this.elems[0], this.elems[3], this.elems[6],
                this.elems[1], this.elems[4], this.elems[7],
                this.elems[2], this.elems[5], this.elems[8]]);
    }
}



// WEBPACK FOOTER //
// ./src/Matrix.ts